import React, { useState, useEffect, useMemo, useRef } from 'react';
import Plot from 'react-plotly.js';
import { getMooringCleanedRollups, getMooringsByGridId } from '../services/mooringService';
import { DEPTH_COLORS, FALLBACK_DEPTH_COLORS, COMPARISON_DEPTH_COLORS } from '../utils/sensorConfig';
import { useAuth } from '../contexts/AuthContext';

const GRID_IDS = [49, 2];

// Every parameter group pairs a Subtidal sensor with a comparison sensor — the
// Turner fluorometers on pc/chla, the legacy "25" probes on temperature/O2.
// `role` marks which of the two is the comparison instrument, so that when a
// buoy reports both, the comparison sensor can be drawn as a dotted overlay —
// the same treatment the water quality charts give the Turner `pc` sensor.
const SENSOR_TYPES = {
  pc:                    { name: 'Phycocyanin T',        unit: 'μg/L',  short: 'Turner',   role: 'comparison' },
  pc_fluorometer:        { name: 'Phycocyanin',          unit: 'μg/L',  short: 'Subtidal', role: 'subtidal'   },
  chla:                  { name: 'Chlorophyll-a T',      unit: 'μg/L',  short: 'Turner',   role: 'comparison' },
  chla_fluorometer:      { name: 'Chlorophyll-a',        unit: 'μg/L',  short: 'Subtidal', role: 'subtidal'   },
  turbidity_fluorometer: { name: 'Turbidity',            unit: 'NTU',   short: 'Subtidal', role: 'subtidal'   },
  temperature:           { name: 'Temperature 25',       unit: '°F',    short: '25',       role: 'comparison' },
  temperature_rdo:       { name: 'Temperature',          unit: '°F',    short: 'RDO',      role: 'subtidal'   },
  o2_25:                 { name: 'Dissolved Oxygen 25',  unit: 'mg/L',  short: '25',       role: 'comparison' },
  o2_rdo:                { name: 'Dissolved Oxygen',     unit: 'mg/L',  short: 'RDO',      role: 'subtidal'   },
  o2_rdo_saturation:     { name: 'O2 Saturation',        unit: '%',     short: 'RDO',      role: 'subtidal'   },
  conductivity:          { name: 'Conductivity',         unit: 'uS/cm', short: null,       role: 'subtidal'   },
};

// A comparison sensor only takes the overlay styling when the primary sensor is
// on the chart too; on its own it's just the normal chart for that parameter.
const depthColors = (sensorKind, overlay) =>
  overlay ? COMPARISON_DEPTH_COLORS : (DEPTH_COLORS[sensorKind] || FALLBACK_DEPTH_COLORS);

const PARAM_GROUPS = [
  { key: 'phycocyanin',   name: 'Phycocyanin',      unit: 'μg/L',  kinds: ['pc', 'pc_fluorometer'] },
  { key: 'chlorophyll',   name: 'Chlorophyll-a',    unit: 'μg/L',  kinds: ['chla', 'chla_fluorometer'] },
  { key: 'temperature',   name: 'Temperature',      unit: '°F',    kinds: ['temperature', 'temperature_rdo'] },
  { key: 'dissolved_o2',  name: 'Dissolved Oxygen', unit: 'mg/L',  kinds: ['o2_25', 'o2_rdo'] },
  { key: 'o2_saturation', name: 'O2 Saturation',    unit: '%',     kinds: ['o2_rdo_saturation'] },
  { key: 'turbidity',     name: 'Turbidity',        unit: 'NTU',   kinds: ['turbidity_fluorometer'] },
  { key: 'conductivity',  name: 'Conductivity',     unit: 'uS/cm', kinds: ['conductivity'] },
];

const ALL_KINDS = Object.keys(SENSOR_TYPES);

const TIME_RANGES = [
  { key: '24h', label: '24h', ms: 24 * 60 * 60 * 1000 },
  { key: '3d',  label: '3d',  ms: 3 * 24 * 60 * 60 * 1000 },
  { key: '1w',  label: '1w',  ms: 7 * 24 * 60 * 60 * 1000 },
  { key: '2w',  label: '2w',  ms: 14 * 24 * 60 * 60 * 1000 },
  { key: '1m',  label: '1m',  ms: 30 * 24 * 60 * 60 * 1000 },
];

const BUOY_COLORS = [
  '#1976d2', '#e53935', '#43a047', '#fb8c00',
  '#8e24aa', '#00acc1', '#6d4c41', '#546e7a',
];

const transformSensorData = (raw) => {
  if (!Array.isArray(raw) || !raw.length) return {};
  return raw.reduce((acc, r) => {
    if (!r?.sensor_kind || r.time === undefined || r.sensor_value === undefined) return acc;
    if (!acc[r.sensor_kind]) acc[r.sensor_kind] = [];
    acc[r.sensor_kind].push({ time: r.time, value: r.sensor_value, depth: r.depth });
    return acc;
  }, {});
};

const applyConversion = (sensorKind, v) => {
  if (sensorKind === 'temperature' || sensorKind === 'temperature_rdo') return v * 9 / 5 + 32;
  if (sensorKind === 'o2_25') return v * 0.032;
  return v;
};

const buildTraces = (transformed, sensorKind, unit, { overlay = false, label = null } = {}) => {
  const readings = transformed[sensorKind];
  if (!readings?.length) return [];

  const sorted = [...readings].sort((a, b) => new Date(a.time) - new Date(b.time));
  const c = depthColors(sensorKind, overlay);
  const traces = [];
  const named = (depthLabel) => (label ? `${label} - ${depthLabel}` : depthLabel);

  const addTrace = (data, name, color, fillcolor) => {
    if (!data.length) return;
    traces.push({
      x: data.map(r => new Date(r.time)),
      y: data.map(r => applyConversion(sensorKind, r.value)),
      type: 'scatter', mode: 'lines', name,
      line: { color, width: 2, ...(overlay && { dash: 'dot' }) },
      // The overlay carries no fill, so the primary sensor's filled areas stay readable.
      ...(fillcolor && { fill: 'tozeroy', fillcolor }),
      hovertemplate: `${name}<br>%{x|%Y-%m-%d %H:%M}<br>%{y:.2f} ${unit}<extra></extra>`,
    });
  };

  const depth1 = sorted.filter(r => Number(r.depth) === 1);
  const depth2 = sorted.filter(r => Number(r.depth) === 2);

  if (depth1.length || depth2.length) {
    // Bottom first so the surface trace draws over it.
    addTrace(depth2, named('Bottom'), c.d2, c.d2fill);
    addTrace(depth1, named('Surface'), c.d1, c.d1fill);
  } else {
    // Buoys that report without a depth fall back to a single line.
    addTrace(sorted, label || SENSOR_TYPES[sensorKind]?.name || 'Value', c.d1, c.d1fill);
  }

  return traces;
};

function Spinner({ size = 28 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      border: `${size <= 16 ? 2 : 3}px solid #e2e8f0`,
      borderTopColor: '#46708a',
      animation: 'spin 0.8s linear infinite',
      flexShrink: 0,
    }} />
  );
}

export default function HQBuoysPage() {
  const { token, sensorPacks } = useAuth();

  const sensorPackDescriptions = useMemo(() => {
    const map = {};
    sensorPacks.forEach(p => {
      if (p.blues_device_id) map[p.blues_device_id] = p.description || p.name || null;
    });
    return map;
  }, [sensorPacks]);

  const [rangeKey, setRangeKey] = useState('3d');
  const [selectedParamKey, setSelectedParamKey] = useState('phycocyanin');
  const [moorings, setMoorings] = useState([]);
  const [mooringsLoading, setMooringsLoading] = useState(true);
  const [mooringsError, setMooringsError] = useState(null);
  const [selectedBuoyIds, setSelectedBuoyIds] = useState(new Set());
  const [buoyDataMap, setBuoyDataMap] = useState({});

  const selectedGroup = PARAM_GROUPS.find(g => g.key === selectedParamKey) || PARAM_GROUPS[0];

  const selectedBuoyIdsKey = useMemo(
    () => Array.from(selectedBuoyIds).sort().join(','),
    [selectedBuoyIds]
  );

  const { startDateISO, endDateISO } = useMemo(() => {
    const end = new Date();
    const range = TIME_RANGES.find(r => r.key === rangeKey);
    const start = new Date(end.getTime() - range.ms);
    return { startDateISO: start.toISOString(), endDateISO: end.toISOString() };
  }, [rangeKey]);

  const prevFetchKey = useRef('');

  useEffect(() => {
    let cancelled = false;
    if (!token) return;
    (async () => {
      try {
        setMooringsLoading(true);
        setMooringsError(null);
        const results = await Promise.all(
          GRID_IDS.map(id =>
            getMooringsByGridId(id, token).then(res =>
              (res.results || res).map(m => ({ ...m, gridId: id }))
            )
          )
        );
        if (cancelled) return;
        const all = results.flat().filter(m => m.sensor_pack != null);
        setMoorings(all);
        setSelectedBuoyIds(new Set(all.map(m => m.id)));
      } catch (err) {
        if (!cancelled) setMooringsError(err.message || 'Failed to load buoys');
      } finally {
        if (!cancelled) setMooringsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    if (!token || moorings.length === 0 || !selectedBuoyIdsKey) return;

    const fetchKey = `${selectedBuoyIdsKey}|${startDateISO}|${endDateISO}`;
    if (fetchKey === prevFetchKey.current) return;
    prevFetchKey.current = fetchKey;

    const selectedMoorings = moorings.filter(m => selectedBuoyIds.has(m.id));

    setBuoyDataMap(prev => {
      const next = {};
      selectedMoorings.forEach(m => {
        next[m.id] = { ...(prev[m.id] || {}), transformed: prev[m.id]?.transformed || {}, loading: true, error: null };
      });
      return next;
    });

    selectedMoorings.forEach(async (m) => {
      try {
        const raw = await getMooringCleanedRollups(m.id, {
          kinds: ALL_KINDS,
          startDate: startDateISO,
          endDate: endDateISO,
        }, token);
        if (cancelled) return;
        setBuoyDataMap(prev => ({
          ...prev,
          [m.id]: { transformed: transformSensorData(Array.isArray(raw) ? raw : []), loading: false, error: null },
        }));
      } catch (err) {
        if (cancelled) return;
        setBuoyDataMap(prev => ({
          ...prev,
          [m.id]: { transformed: {}, loading: false, error: err.message },
        }));
      }
    });

    return () => { cancelled = true; };
  }, [token, moorings, selectedBuoyIdsKey, startDateISO, endDateISO, rangeKey]);

  const toggleBuoy = (id) => {
    setSelectedBuoyIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedBuoyIds(new Set(moorings.map(m => m.id)));

  const selectedMoorings = useMemo(
    () => moorings.filter(m => selectedBuoyIds.has(m.id)),
    [moorings, selectedBuoyIds]
  );

  const anyLoading = Object.values(buoyDataMap).some(s => s.loading);
  const rangeLabel = TIME_RANGES.find(r => r.key === rangeKey)?.label || rangeKey;

  if (mooringsLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f6f5f1', paddingTop: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, flexDirection: 'column' }}>
        <Spinner size={32} />
        <span style={{ fontSize: 15, color: '#6f6a5f' }}>Loading HQ buoys…</span>
      </div>
    );
  }

  if (mooringsError) {
    return (
      <div style={{ minHeight: '100vh', background: '#f6f5f1', paddingTop: 64 }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', padding: '24px 24px' }}>
          <div style={{ background: '#fff', border: '1px solid #f0cfc9', borderRadius: 14, padding: '40px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>⚠</span>
            <span style={{ color: '#9e2b25', fontSize: 15, fontWeight: 600 }}>Failed to load buoys</span>
            <span style={{ color: '#6f6a5f', fontSize: 14, textAlign: 'center', maxWidth: 380 }}>{mooringsError}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f6f5f1', paddingTop: 64, fontFamily: "'Outfit', sans-serif" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '24px 24px 48px' }}>

        {/* ── Controls panel ── */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #ece7db', padding: '20px 24px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,.03)' }}>

          {/* Title + time range */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 18 }}>
            <div>
              <h1 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 26, fontWeight: 600, color: '#1f2a2e', margin: 0, letterSpacing: '-0.01em' }}>
                HQ Buoys
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <span style={{ fontSize: 14, color: '#6f6a5f' }}>Grids 49 &amp; 2</span>
                {anyLoading && <Spinner size={14} />}
              </div>
            </div>
            {/* Range selector */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {TIME_RANGES.map(r => (
                <button key={r.key} onClick={() => setRangeKey(r.key)} style={{
                  padding: '6px 14px', borderRadius: 100, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
                  fontWeight: rangeKey === r.key ? 600 : 400,
                  background: rangeKey === r.key ? '#1e293b' : '#fff',
                  color: rangeKey === r.key ? '#fff' : '#64748b',
                  border: rangeKey === r.key ? '1px solid #1e293b' : '1px solid #e2e8f0',
                }}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Param group selector */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
            {PARAM_GROUPS.map(g => (
              <button key={g.key} onClick={() => setSelectedParamKey(g.key)} style={{
                padding: '6px 14px', borderRadius: 100, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
                fontWeight: selectedParamKey === g.key ? 600 : 400,
                background: selectedParamKey === g.key ? '#46708a' : '#fff',
                color: selectedParamKey === g.key ? '#fff' : '#46505a',
                border: selectedParamKey === g.key ? '1px solid #46708a' : '1px solid #e2e8f0',
              }}>
                {g.name}
              </button>
            ))}
          </div>

          <div style={{ height: 1, background: '#ece7db', marginBottom: 18 }} />

          {/* Buoy filter chips */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#6f6a5f', marginRight: 4 }}>Buoys:</span>
            {moorings.map((m, idx) => {
              const color = BUOY_COLORS[idx % BUOY_COLORS.length];
              const active = selectedBuoyIds.has(m.id);
              const desc = sensorPackDescriptions[m.sensor_pack];
              const chipLabel = [`Buoy ${m.buoy || m.id}`, desc, `Grid ${m.gridId}`].filter(Boolean).join('  ·  ');
              return (
                <button key={m.id} onClick={() => toggleBuoy(m.id)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: active ? 600 : 400,
                  cursor: 'pointer', fontFamily: 'inherit',
                  background: active ? `${color}18` : 'transparent',
                  color: active ? color : '#9a9384',
                  border: `1.5px solid ${active ? color : 'rgba(0,0,0,0.12)'}`,
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: active ? color : 'rgba(0,0,0,0.2)', flexShrink: 0 }} />
                  {chipLabel}
                </button>
              );
            })}
            {selectedBuoyIds.size < moorings.length && (
              <button onClick={selectAll} style={{
                padding: '4px 12px', borderRadius: 999, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit',
                background: 'transparent', color: '#46708a', border: '1px solid #c8dce6', fontWeight: 500,
              }}>
                Show all
              </button>
            )}
          </div>

        </div>

        {/* ── Per-buoy charts ── */}
        {selectedMoorings.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #ece7db', padding: '40px 24px', textAlign: 'center' }}>
            <span style={{ fontSize: 14, color: '#8b8475' }}>No buoys selected. Use the filter above to choose which buoys to display.</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {selectedMoorings.map((m) => {
              const colorIdx = moorings.findIndex(mo => mo.id === m.id);
              const color = BUOY_COLORS[colorIdx % BUOY_COLORS.length];
              const state = buoyDataMap[m.id];
              const desc = sensorPackDescriptions[m.sensor_pack];

              // A buoy can carry more than one instrument for the same parameter
              // (e.g. both `pc` and `pc_fluorometer`); draw all of them.
              const activeSensorKinds = state && !state.loading
                ? selectedGroup.kinds.filter(k => state.transformed[k]?.length > 0)
                : [];
              // Only overlay once both instruments are actually on the chart.
              const hasBoth = activeSensorKinds.length > 1;
              const isOverlay = (kind) => hasBoth && SENSOR_TYPES[kind]?.role === 'comparison';
              const traces = activeSensorKinds.flatMap(kind =>
                buildTraces(state.transformed, kind, selectedGroup.unit, {
                  overlay: isOverlay(kind),
                  label: isOverlay(kind) ? SENSOR_TYPES[kind]?.short : null,
                })
              );

              const allY = traces.flatMap(t => t.y || []).filter(Number.isFinite);
              // Seed with the first value (not 0) — an all-negative or all-positive series
              // shouldn't have its max/min artificially clamped to zero.
              const yMax = allY.length ? allY.reduce((a, b) => Math.max(a, b)) : 0;
              const yMin = allY.length ? allY.reduce((a, b) => Math.min(a, b)) : 0;
              const isPcLike = ['phycocyanin', 'chlorophyll'].includes(selectedGroup.key);
              const yRange = yMin < 0
                // Any sensor type: when readings dip below zero, size the axis around the
                // actual data so negative excursions are visible rather than clipped at 0.
                ? [yMin * 1.1, Math.max(yMax * 1.1, Math.abs(yMin) * 1.1)]
                : (isPcLike ? [0, Math.max(3, yMax * 1.1)] : undefined);


              return (
                <div key={m.id} style={{ background: '#fff', borderRadius: 14, border: '1px solid #ece7db', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,.03)' }}>
                  {/* Buoy header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#1f2a2e' }}>Buoy {m.buoy || m.id}</span>
                    {desc && <span style={{ fontSize: 14, fontWeight: 600, color }}>{desc}</span>}
                    <span style={{ fontSize: 13, color: '#8b8475' }}>Grid {m.gridId}</span>
                    {activeSensorKinds.map(kind => {
                      const sensor = SENSOR_TYPES[kind];
                      if (!sensor) return null;
                      const badge = depthColors(kind, isOverlay(kind)).d1;
                      return (
                        <span key={kind} style={{
                          fontSize: 11.5, fontWeight: 600, padding: '2px 9px', borderRadius: 999,
                          background: `${badge}14`, color: badge,
                          border: `1px solid ${badge}40`,
                        }}>
                          {sensor.name}
                        </span>
                      );
                    })}
                    {state?.loading && <Spinner size={16} />}
                  </div>

                  {/* Chart body */}
                  {!state || state.loading ? (
                    <div style={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Spinner size={28} />
                    </div>
                  ) : state.error ? (
                    <div style={{ background: '#fff8f8', border: '1px solid #f0cfc9', borderRadius: 10, padding: '20px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 18 }}>⚠</span>
                      <span style={{ color: '#9e2b25', fontSize: 14 }}>{state.error}</span>
                    </div>
                  ) : traces.length === 0 ? (
                    <div style={{ background: '#f8f7f4', borderRadius: 10, padding: '32px 16px', textAlign: 'center' }}>
                      <span style={{ fontSize: 14, color: '#8b8475' }}>
                        No {selectedGroup.name.toLowerCase()} data for the past {rangeLabel}.
                      </span>
                    </div>
                  ) : (
                    <Plot
                      data={traces}
                      layout={{
                        height: 280,
                        margin: { l: 20, r: 20, t: 10, b: 60 },
                        xaxis: { type: 'date', tickformat: '%b %d %-I%p', automargin: true, range: [startDateISO, endDateISO] },
                        yaxis: {
                          title: { text: `${selectedGroup.name} (${selectedGroup.unit})` },
                          automargin: true,
                          ...(yRange && { range: yRange }),
                        },
                        showlegend: true,
                        legend: { orientation: 'h', x: 0.5, xanchor: 'center', y: -0.25 },
                        paper_bgcolor: 'rgba(0,0,0,0)',
                        plot_bgcolor: 'rgba(0,0,0,0)',
                        hovermode: 'x unified',
                      }}
                      config={{ displayModeBar: false, responsive: true }}
                      style={{ width: '100%' }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
